import React, { useState, useEffect, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createTenderSchema, TenderFormData } from "../schema/tender-schema";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Form } from "@/app/global-components/form";
import OfferSubmitView from "./tender-submit-view";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { isEmpty } from "lodash";
import regionsData from "@/app/features/company/data/regions_lite.json";
import citiesData from "@/app/features/company/data/cities_lite.json";
import CompanyWarningPopup from "@/app/features/CompanyCreate/companycreatePopup";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { routes } from "@/consts";
import useRegisterCompany from "@/hooks/use-register-company";
import useOffer from "@/hooks/useTender";
import { updateTender } from "@/store/authSlice";
import {
  Box,
  DialogContent,
  Button,
  Dialog,
  DialogTitle,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  FormControl,
  DialogActions,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  IconButton,
  FormHelperText,
} from "@mui/material";
import { useShipping } from "@/hooks/use-shipping";
import ItemsTender from "./items.tender";
import { LANG } from "@/types";
import {
  Tag,
  Calendar,
  FileText,
  Plus,
  AlertCircle,
  Save,
  X,
  Search,
} from "lucide-react";

interface Address {
  id: string;
  fullName: string;
  address: string;
  region: string;
  city: string;
  phoneNumber: string;
  isDefault: boolean;
}
interface Product {
  item: string;
  quantity: number;
  notice: string;
  unit: string;
  attachment: File | null;
}
interface CategoryOption {
  value: any;
  label: any;
}

interface Region {
  region_id: number;
  name_ar: string;
}

interface City {
  city_id: number;
  region_id: number;
  name_ar: string;
}

interface Option {
  value: string;
  label: string;
}

const TenderFormView: React.FC = () => {
  const { locale } = useLocale();
  const { tender } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const { t } = useTranslate(locale);
  const TenderSchema = createTenderSchema(t as (key: string) => string);
  const navigate = useNavigate();
  const { createOffer, getcategories, isSaveingLoading, error } = useOffer();
  const [products, setProducts] = useState<Product[]>([
    { item: "", quantity: 1, notice: "", unit: "", attachment: null },
  ]);
  const prevValuesRef = useRef<any>({});
  const { myShipping, createShipping } = useShipping();

  const [selectedAddress, setSelectedAddress] = useState<string>("");
  const [addresses, setAddresses] = useState<any[]>([]);
  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const data = await myShipping();
      if (data) {
        setAddresses(data);
        const defaultAddress = data.find((addr: any) => addr.isDefault);
        if (defaultAddress) {
          setSelectedAddress(defaultAddress.id);
        }
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };
  const [Categoris, setCategoris] = useState<CategoryOption[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isCompanyAvailable, setIsCompanyAvailable] = useState(false);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const { getCompanyMine } = useRegisterCompany();
  const form = useForm<z.infer<typeof TenderSchema>>({
    resolver: zodResolver(TenderSchema),
    defaultValues: {
      id: null,
      title: tender.title,
      value: tender.value || undefined,
      endDate: tender.endDate,
      addressId: tender.addressId,
      openingDate: tender.openingDate,
      type: "Tiny",
      status: null,
      region: "",
      city: "",
      Paylater: "No",
      categoriesIds: tender.categoriesIds,
      fileId: null,
      products: tender.products,
      companyId: myCompany?.id,
      TenderNr: myCompany?.TenderNr + 1,
    },
  });
  const values = form.watch();
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      // Set the first file as the fileId for the form
      form.setValue("fileId", files[0] || null);
    }
  };
  useEffect(() => {
    const currentValues = {
      title: values.title,
      products: products,
      categoriesIds: values.categoriesIds,
      endDate: values.endDate,
      openingDate: values.openingDate,
      value: values.value,
    };

    const prevValues = prevValuesRef.current;
    const updates: Partial<typeof tender> = {};

    if (prevValues.title !== currentValues.title) {
      updates.title = currentValues.title;
    }
    if (
      JSON.stringify(prevValues.products) !==
      JSON.stringify(currentValues.products)
    ) {
      updates.products = currentValues.products;
    }
    if (
      JSON.stringify(prevValues.categoriesIds) !==
      JSON.stringify(currentValues.categoriesIds)
    ) {
      updates.categoriesIds = currentValues.categoriesIds;
    }
    if (prevValues.endDate !== currentValues.endDate) {
      updates.endDate = currentValues.endDate;
    }
    if (prevValues.openingDate !== currentValues.openingDate) {
      updates.openingDate = currentValues.openingDate;
    }
    if (prevValues.value !== currentValues.value) {
      updates.value = currentValues.value;
    }

    if (Object.keys(updates).length > 0) {
      dispatch(updateTender({ ...tender, ...updates }));
    }

    prevValuesRef.current = currentValues;
  }, [
    values.title,
    products,
    values.categoriesIds,
    values.endDate,
    values.openingDate,
    values.value,
    dispatch,
  ]);
  useEffect(() => {
    const fetchCompany = async () => {
      await getCompanyMine().catch(() => {
        setIsCompanyAvailable(true);
      });
    };

    const fetchCategoris = async () => {
      try {
        const Categories = await getcategories();
        if (Categories) {
          const uniqueCategories = Categories.data.filter(
            (item: { name: any }, index: any, self: any[]) =>
              index === self.findIndex((t) => t.name === item.name)
          );
          const Cates: CategoryOption[] = uniqueCategories.map(
            (item: { id: any; name: any }) => ({
              value: item?.id,
              label: item.name,
            })
          );
          setCategoris(Cates);
        } else {
          setCategoris([]);
        }
      } catch (error) {
        console.error("Error fetching Offers:", error);
      }
    };
    fetchCompany();
    fetchCategoris();
  }, []);

  useEffect(() => {
    if (myCompany) {
      form.setValue("companyId", myCompany.id);
      form.setValue("TenderNr", myCompany.TenderNr + 1);
      form.setValue("region", myCompany.region);
      form.setValue("city", myCompany.city);
    }
  }, [myCompany]);
  const [newAddress, setNewAddress] = useState<Partial<Address>>({
    fullName: "",
    address: "",
    region: "",
    city: "",
    phoneNumber: "",
    isDefault: false,
  });
  const addProduct = () => {
    const newProduct = {
      item: "",
      quantity: 1,
      notice: "",
      unit: "",
      attachment: null,
    };
    const currentProducts = form.getValues("products") as Product[];
    const updatedProducts = [...currentProducts, newProduct];
    setProducts(updatedProducts);

    form.setValue("products", updatedProducts);
  };
  const [cities, setCities] = useState<Option[]>([]);
  useEffect(() => {
    const selectedRegion = newAddress.region;
    if (selectedRegion) {
      const filteredCities = citiesData
        .filter((city: City) => city.region_id === parseInt(selectedRegion))
        .map((city: City) => ({
          value: city?.city_id.toString(),
          label: city.name_ar,
        }));
      setCities(filteredCities);
    } else {
      setCities([]);
    }
  }, [newAddress.region]);

  const handleAddAddress = async () => {
    try {
      const response = await createShipping({
        fullName: newAddress.fullName || "",
        address: newAddress.address || "",
        region: newAddress.region || "",
        phoneNumber: newAddress.phoneNumber || "",
        city: newAddress.city || "",
        isDefault: newAddress.isDefault || false,
      });

      if (response) {
        await fetchAddresses();
        setIsAddDialogOpen(false);
        setNewAddress({
          fullName: "",
          address: "",
          region: "",
          city: "",
          phoneNumber: "",
          isDefault: false,
        });
      }
    } catch (error) {
      console.error("Error adding address:", error);
    }
  };
  const onSubmit = async (values: TenderFormData) => {
    try {
      await createOffer({
        ...values,
        addressId: selectedAddress,
      });
      dispatch(
        updateTender({
          id: null,
          TenderNr: null,
          title: "",
          status: null,
          value: 0,
          endDate: "",
          openingDate: "",
          type: "Tiny",
          region: "",
          city: "",
          Paylater: "No",
          categoriesIds: [],
          fileId: null,
          products: [],
          companyId: null,
        })
      );
      navigate(routes.tenders);
    } catch (err) {
      console.error("Error creating offer:", err);
    }
  };
  const regions = useMemo(
    () =>
      regionsData.map((region: Region) => ({
        value: region.region_id.toString(),
        label: region.name_ar,
      })),
    []
  );
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [filteredCategories, setFilteredCategories] = useState<
    CategoryOption[]
  >([]);

  // Function to check if all required fields are filled
  const isFormValid = () => {
    const values = form.getValues();
    const hasTitle = values.title && values.title.trim() !== "";
    const hasCategories =
      values.categoriesIds && values.categoriesIds.length > 0;
    const hasEndDate = values.endDate && values.endDate.trim() !== "";
    const hasOpeningDate =
      values.openingDate && values.openingDate.trim() !== "";
    const hasAddress = selectedAddress && selectedAddress.trim() !== "";
    const hasProducts = values.products && values.products.length > 0;

    // Check if at least one product has required fields
    const hasValidProducts =
      hasProducts &&
      values.products?.some(
        (product: any) =>
          product.item &&
          product.item.trim() !== "" &&
          product.quantity &&
          product.quantity > 0
      );

    return (
      hasTitle &&
      hasCategories &&
      hasEndDate &&
      hasOpeningDate &&
      hasAddress &&
      hasValidProducts
    );
  };

  useEffect(() => {
    const filterCategories = () => {
      const filtered = Categoris.filter((category) =>
        category.label.toLowerCase().includes(categorySearch.toLowerCase())
      );
      setFilteredCategories(filtered);
    };

    filterCategories();
  }, [Categoris, categorySearch]);

  return (
    <Form {...form}>
      {isCompanyAvailable && <CompanyWarningPopup open={true} />}
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box sx={{ p: 4 }}>
          {error && (
            <Alert
              severity="error"
              icon={<AlertCircle size={20} />}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: "none",
                  border: "1px solid #e5e7eb",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 3, fontWeight: 600, color: "#374151" }}
                  >
                    {t("general.purchaseRequest")} {t("general.information")}
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={
                          t("orders.placeholders.enterRequestTitle") + " *"
                        }
                        {...form.register("title")}
                        error={!!form.formState.errors.title}
                        helperText={form.formState.errors.title?.message}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        inputProps={{
                          dir: locale === LANG.AR ? "rtl" : "ltr",
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        error={!!form.formState.errors.categoriesIds}
                      >
                        <InputLabel
                          dir={locale === LANG.AR ? "rtl" : "ltr"}
                          sx={{
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          }}
                        >
                          {t("company.fields.categories")} *
                        </InputLabel>
                        <Select
                          multiple
                          open={isCategoryDropdownOpen}
                          onOpen={() => setIsCategoryDropdownOpen(true)}
                          onClose={() => {
                            setIsCategoryDropdownOpen(false);
                            setCategorySearch("");
                          }}
                          value={form.watch("categoriesIds") || []}
                          onChange={(e) =>
                            form.setValue(
                              "categoriesIds",
                              e.target.value as string[]
                            )
                          }
                          label={t("company.fields.categories")}
                          dir={locale === LANG.AR ? "rtl" : "ltr"}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                              },
                            },
                            onMouseDown: (e) => {
                              e.stopPropagation();
                            },
                            disableRestoreFocus: true,
                            keepMounted: true,
                          }}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {(selected as string[]).map((value: string) => {
                                const category = Categoris.find(
                                  (cat) => cat.value === value
                                );
                                return category ? (
                                  <Chip
                                    key={value}
                                    label={category.label}
                                    size="small"
                                    sx={{
                                      backgroundColor: "#e0e7ff",
                                      color: "#3730a3",
                                      fontSize: "0.75rem",
                                    }}
                                  />
                                ) : null;
                              })}
                            </Box>
                          )}
                          sx={{
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            "& .MuiSelect-select": {
                              paddingRight: "10px !important",
                            },
                            "& .MuiSelect-icon": {
                              left: locale === LANG.EN ? "unset" : 0,
                              marginLeft: 0,
                              right: locale === LANG.EN ? 0 : "unset",
                            },
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                            "& legend": {
                              textAlign: locale === LANG.AR ? "end" : "start",
                            },
                          }}
                        >
                          <Box sx={{ p: 1, borderBottom: "1px solid #e5e7eb" }}>
                            <TextField
                              size="small"
                              placeholder={
                                locale === LANG.EN
                                  ? "Search categories..."
                                  : "البحث في الفئات..."
                              }
                              value={categorySearch}
                              onChange={(e) => {
                                setCategorySearch(e.target.value);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              InputProps={{
                                startAdornment: (
                                  <Search
                                    size={16}
                                    style={{ marginRight: 8, color: "#6b7280" }}
                                  />
                                ),
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1,
                                },
                              }}
                            />
                          </Box>
                          {filteredCategories.map((category) => (
                            <MenuItem
                              key={category.value}
                              value={category.value}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              sx={{
                                borderRadius: 1,
                                margin: "2px 0",
                                "&.Mui-selected": {
                                  backgroundColor: "#644980",
                                  color: "#fff",
                                  fontWeight: "600",
                                  "&:hover": {
                                    backgroundColor: "#644980",
                                  },
                                },
                                "&:hover": {
                                  backgroundColor: "#f3f4f6",
                                },
                              }}
                            >
                              {category.label}
                            </MenuItem>
                          ))}
                          {filteredCategories.length === 0 && (
                            <MenuItem
                              disabled
                              sx={{ textAlign: "center", color: "#6b7280" }}
                            >
                              {locale === LANG.EN
                                ? "No categories found"
                                : "لم يتم العثور على فئات"}
                            </MenuItem>
                          )}
                        </Select>
                        {form.formState.errors.categoriesIds && (
                          <FormHelperText>
                            {form.formState.errors.categoriesIds.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("tender.fields.estimatedValue") + " *"}
                        type="number"
                        inputProps={{
                          min: 0,
                          dir: locale === LANG.AR ? "rtl" : "ltr",
                        }}
                        {...form.register("value")}
                        error={!!form.formState.errors.value}
                        helperText={form.formState.errors.value?.message}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        InputProps={{
                          startAdornment:
                            locale === LANG.EN ? (
                              <Tag
                                size={16}
                                style={{ marginRight: 8, color: "#6b7280" }}
                              />
                            ) : null,
                          endAdornment:
                            locale === LANG.AR ? (
                              <Tag
                                size={16}
                                style={{ marginLeft: 8, color: "#6b7280" }}
                              />
                            ) : null,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="file"
                        onChange={handleFileChange}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        inputProps={{
                          dir: locale === LANG.AR ? "rtl" : "ltr",
                        }}
                        InputProps={{
                          startAdornment:
                            locale === LANG.EN ? (
                              <FileText
                                size={16}
                                style={{ marginRight: 8, color: "#6b7280" }}
                              />
                            ) : null,
                          endAdornment:
                            locale === LANG.AR ? (
                              <FileText
                                size={16}
                                style={{ marginLeft: 8, color: "#6b7280" }}
                              />
                            ) : null,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            paddingRight: "12px",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Items Section */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: "none",
                  border: "1px solid #e5e7eb",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "#374151" }}
                    >
                      {t("offers.items")}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Plus size={16} />}
                      onClick={addProduct}
                      sx={{
                        background: "#18181b",
                        color: "white",
                        borderRadius: 2,
                      }}
                    >
                      {t("actions.addItem")}
                    </Button>
                  </Box>

                  {products.length > 0 && (
                    <ItemsTender
                      products={products}
                      form={form}
                      setProducts={setProducts}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Dates and Address Section */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: "none",
                  border: "1px solid #e5e7eb",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 3, fontWeight: 600, color: "#374151" }}
                  >
                    {t("offers.fields.submissionDeadline")} &{" "}
                    {t("checkout.fields.shippingAddress")}
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("offers.fields.submissionDeadline")}
                        type="date"
                        {...form.register("endDate")}
                        error={!!form.formState.errors.endDate}
                        helperText={form.formState.errors.endDate?.message}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        inputProps={{
                          min: new Date(
                            new Date().setDate(new Date().getDate() + 1)
                          )
                            .toISOString()
                            .split("T")[0],
                          dir: locale === LANG.AR ? "rtl" : "ltr",
                        }}
                        InputProps={{
                          startAdornment:
                            locale === LANG.EN ? (
                              <Calendar
                                size={16}
                                style={{ marginRight: 8, color: "#6b7280" }}
                              />
                            ) : null,
                          endAdornment:
                            locale === LANG.AR ? (
                              <Calendar
                                size={16}
                                style={{ marginLeft: 8, color: "#6b7280" }}
                              />
                            ) : null,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            paddingRight: "12px",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            "&[data-shrink='false']": {
                              pr: locale === LANG.AR ? "20px" : "0px",
                            },
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label={t("offers.fields.openingDate")}
                        {...form.register("openingDate")}
                        error={!!form.formState.errors.openingDate}
                        helperText={form.formState.errors.openingDate?.message}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        disabled={!form.watch("endDate")}
                        inputProps={{
                          min: (() => {
                            const endDate = form.watch("endDate");
                            if (!endDate) return "";

                            try {
                              const date = new Date(endDate);
                              if (isNaN(date.getTime())) return "";

                              date.setDate(date.getDate() + 1);
                              return date.toISOString().split("T")[0];
                            } catch (error) {
                              return "";
                            }
                          })(),
                          dir: locale === LANG.AR ? "rtl" : "ltr",
                        }}
                        InputProps={{
                          startAdornment:
                            locale === LANG.EN ? (
                              <Calendar
                                size={16}
                                style={{ marginRight: 8, color: "#6b7280" }}
                              />
                            ) : null,
                          endAdornment:
                            locale === LANG.AR ? (
                              <Calendar
                                size={16}
                                style={{ marginLeft: 8, color: "#6b7280" }}
                              />
                            ) : null,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            paddingRight: "12px",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            "&[data-shrink='false']": {
                              pr: locale === LANG.AR ? "20px" : "0px",
                            },
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      {addresses.length > 0 ? (
                        <FormControl fullWidth error={!selectedAddress}>
                          <InputLabel
                            dir={locale === LANG.AR ? "rtl" : "ltr"}
                            sx={{
                              transformOrigin:
                                locale === LANG.AR ? "right" : "left",
                              direction: locale === LANG.AR ? "rtl" : "ltr",
                              ...(locale === LANG.AR
                                ? { right: 30, left: "auto" }
                                : { left: 0, right: "auto" }),
                            }}
                          >
                            {t("checkout.fields.shippingAddress")} *
                          </InputLabel>
                          <Select
                            value={selectedAddress}
                            onChange={(e) => setSelectedAddress(e.target.value)}
                            label={t("checkout.fields.shippingAddress")}
                            dir={locale === LANG.AR ? "rtl" : "ltr"}
                            sx={{
                              borderRadius: 2,
                              direction: locale === LANG.AR ? "rtl" : "ltr",
                              "& .MuiSelect-select": {
                                paddingRight: "10px !important",
                              },
                              "& .MuiSelect-icon": {
                                left: locale === LANG.EN ? "unset" : 0,
                                marginLeft: 0,
                                right: locale === LANG.EN ? 0 : "unset",
                              },
                              "&:hover": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: !selectedAddress
                                    ? "#ef4444"
                                    : "#644980",
                                },
                              },
                              "&.Mui-focused": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: !selectedAddress
                                    ? "#ef4444"
                                    : "#644980",
                                  borderWidth: 2,
                                },
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: !selectedAddress
                                  ? "#ef4444"
                                  : "#d1d5db",
                              },
                              "& legend": {
                                textAlign: locale === LANG.AR ? "end" : "start",
                              },
                            }}
                          >
                            <MenuItem value="" disabled>
                              {t("checkout.fields.selectShippingAddress")}
                            </MenuItem>
                            {addresses.map((address) => (
                              <MenuItem key={address.id} value={address.id}>
                                {address.fullName} - {address.address}
                              </MenuItem>
                            ))}
                          </Select>
                          {!selectedAddress && (
                            <FormHelperText sx={{ color: "#ef4444" }}>
                              {t("checkout.fields.shippingAddressRequired")}
                            </FormHelperText>
                          )}
                        </FormControl>
                      ) : (
                        <Button
                          variant="outlined"
                          startIcon={
                            locale === LANG.EN ? <Plus size={16} /> : null
                          }
                          endIcon={
                            locale === LANG.AR ? <Plus size={16} /> : null
                          }
                          onClick={() => setIsAddDialogOpen(true)}
                          fullWidth
                          dir={locale === LANG.AR ? "rtl" : "ltr"}
                          sx={{
                            borderColor: "#ef4444",
                            color: "#ef4444",
                            borderRadius: 2,
                            py: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            "&:hover": {
                              borderColor: "#dc2626",
                              color: "#dc2626",
                              backgroundColor: "#fef2f2",
                            },
                          }}
                        >
                          {t("checkout.fields.addShippingAddress")}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Payment Terms Section */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: "none",
                  border: "1px solid #e5e7eb",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 3, fontWeight: 600, color: "#374151" }}
                  >
                    {t("offers.fields.paymentTerms")}
                  </Typography>

                  <FormControl fullWidth>
                    <InputLabel
                      dir={locale === LANG.AR ? "rtl" : "ltr"}
                      sx={{
                        transformOrigin: locale === LANG.AR ? "right" : "left",
                        direction: locale === LANG.AR ? "rtl" : "ltr",
                        ...(locale === LANG.AR
                          ? { right: 30, left: "auto" }
                          : { left: 0, right: "auto" }),
                      }}
                    >
                      {t("offers.fields.paymentTerms")}
                    </InputLabel>
                    <Select
                      value={form.watch("Paylater") || "No"}
                      onChange={(e) =>
                        form.setValue(
                          "Paylater",
                          e.target.value as "Yes" | "No"
                        )
                      }
                      label={t("offers.fields.paymentTerms")}
                      dir={locale === LANG.AR ? "rtl" : "ltr"}
                      sx={{
                        borderRadius: 2,
                        direction: locale === LANG.AR ? "rtl" : "ltr",
                        "& .MuiSelect-select": {
                          paddingRight: "10px !important",
                        },
                        "& .MuiSelect-icon": {
                          left: locale === LANG.EN ? "unset" : 0,
                          marginLeft: 0,
                          right: locale === LANG.EN ? 0 : "unset",
                        },
                        "&:hover": {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#644980",
                          },
                        },
                        "&.Mui-focused": {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#644980",
                            borderWidth: 2,
                          },
                        },
                        "& legend": {
                          textAlign: locale === LANG.AR ? "end" : "start",
                        },
                      }}
                    >
                      <MenuItem value="Yes">{t("payment.deferred")}</MenuItem>
                      <MenuItem value="No">
                        {t("offers.fields.cashPayment")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Submit Button */}
          {!isEmpty(myCompany) && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
              <OfferSubmitView
                isLoading={isSaveingLoading}
                disabled={!isFormValid()}
              />
            </Box>
          )}
        </Box>

        {/* Add Address Dialog */}
        <Dialog
          dir={locale === LANG.AR ? "rtl" : "ltr"}
          open={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: "90vh",
              m: 2,
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
              borderBottom: "1px solid #e5e7eb",
              p: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#374151" }}
              >
                {t("delivery.addNewAddress")}
              </Typography>
              <IconButton
                onClick={() => setIsAddDialogOpen(false)}
                sx={{
                  color: "#6b7280",
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                  },
                }}
              >
                <X size={20} />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("profile.fields.nickName")}
                  value={newAddress.fullName}
                  onChange={(e) =>
                    setNewAddress({ ...newAddress, fullName: e.target.value })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                          borderWidth: 2,
                        },
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("company.fields.address")}
                  value={newAddress.address}
                  onChange={(e) =>
                    setNewAddress({ ...newAddress, address: e.target.value })
                  }
                  multiline
                  rows={2}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                          borderWidth: 2,
                        },
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t("company.fields.region")}</InputLabel>
                  <Select
                    value={newAddress.region}
                    label={t("company.fields.region")}
                    onChange={(e) =>
                      setNewAddress({ ...newAddress, region: e.target.value })
                    }
                    sx={{
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                          borderWidth: 2,
                        },
                      },
                    }}
                  >
                    {regions.map((region) => (
                      <MenuItem key={region.value} value={region.value}>
                        {region.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t("company.fields.city")}</InputLabel>
                  <Select
                    value={newAddress.city}
                    label={t("company.fields.city")}
                    onChange={(e) =>
                      setNewAddress({ ...newAddress, city: e.target.value })
                    }
                    sx={{
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                          borderWidth: 2,
                        },
                      },
                    }}
                  >
                    {cities.map((city) => (
                      <MenuItem key={city.value} value={city.value}>
                        {city.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("profile.fields.phoneNumber")}
                  value={newAddress.phoneNumber}
                  onChange={(e) =>
                    setNewAddress({
                      ...newAddress,
                      phoneNumber: e.target.value,
                    })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#644980",
                          borderWidth: 2,
                        },
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
              borderTop: "1px solid #e5e7eb",
              gap: 2,
            }}
          >
            <Button
              onClick={() => setIsAddDialogOpen(false)}
              variant="outlined"
              sx={{
                borderColor: "#d1d5db",
                color: "#6b7280",
                borderRadius: 2,
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              {t("actions.cancel")}
            </Button>
            <Button
              onClick={handleAddAddress}
              variant="contained"
              startIcon={<Save size={16} />}
              sx={{
                background: "linear-gradient(135deg, #644980 0%, #1d4ed8 100%)",
                color: "white",
                borderRadius: 2,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #644980 0%, #1e40af 100%)",
                },
              }}
            >
              {t("actions.save")}
            </Button>
          </DialogActions>
        </Dialog>
      </form>
    </Form>
  );
};

export default TenderFormView;
